/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-general
 * @responsibility Serialize the SET-F04 organization-general update wire command and response.
 * @state-owner authenticated API
 * @allowed-imports API client public type; @vispo/contracts
 * @forbidden React; UI role inference; member fields
 * @public-api updateOrganization, uploadOrganizationLogo
 * @side-effects authenticated HTTP request
 * @tests frontend/packages/workspace-web/src/modules/settings-general/components/GeneralSettingsSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import type { VispoApiClient } from '@vispo/api-client';
import {
  type UpdateOrganizationInput,
  updateOrganizationInputSchema,
  updateOrganizationResponseSchema,
} from '@vispo/contracts';

export function updateOrganization(
  apiClient: VispoApiClient,
  organizationId: string,
  input: UpdateOrganizationInput,
) {
  return apiClient
    .request<unknown>(`/api/v1/organizations/${organizationId}`, {
      method: 'PATCH',
      body: updateOrganizationInputSchema.parse(input),
    })
    .then((response) => updateOrganizationResponseSchema.parse(response));
}

// SET-F11: raw-octet-stream-body + header transport, not multipart/form-data -- mirrors
// upload_single_question_image, this backend's own established binary-upload precedent
// (activities/router.py), rather than introducing a new, unprecedented request shape.
export function uploadOrganizationLogo(
  apiClient: VispoApiClient,
  organizationId: string,
  file: File,
  options: { expectedRevision: number; idempotencyKey: string },
) {
  return apiClient
    .request<unknown>(`/api/v1/organizations/${organizationId}/logo`, {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type,
        'X-Expected-Revision': String(options.expectedRevision),
        'X-Idempotency-Key': options.idempotencyKey,
      },
    })
    .then((response) => updateOrganizationResponseSchema.parse(response));
}
